import { useId, useRef, useState, type ReactNode } from 'react';

import { formatNumber, formatPercent } from '@shared/format';
import { cn } from '@shared/ui/cn';
import { DTable, DTableBody, DTableHead, DTableRowHeader, DTableScroll } from '@shared/ui/dtable';
import { StateBadge } from '@shared/ui/StateBadge';

import { resolveArtworkPurpose, resolveArtworkSource, resolveCreditRole } from './constants';
import { sharedCopy } from './copy';
import type { ArtworkPurposeRow, CreditProfileRow, CreditRelation } from './types';

/**
 * The fragments more than one content surface renders the same way.
 *
 * `SurfaceError` is **not** here — it reached the shared ring on its third
 * occurrence and is imported from `@shared/ui/SurfaceError`. `TruncationLine`
 * stays local for the reason that promotion records: its sentence differs per
 * surface, so the copies share a shape and not a reason to change.
 */

/** The text-input shape every content form uses, so no two fields drift apart. */
export const FIELD_CONTROL_CLASS =
  'hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink';

/**
 * A labelled control with its hint, its error, and its required mark.
 *
 * **The error joins `aria-describedby` when it appears and leaves when it
 * goes**, so no hidden text is announced — a permanent association pointing at
 * an empty node reads as a field that always has something wrong with it.
 *
 * The required mark is `aria-hidden` and `aria-required` carries the fact,
 * because a lone asterisk read aloud is a punctuation mark rather than a
 * requirement.
 */
export function Field({
  label,
  hint,
  error,
  required = false,
  wide = false,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  /**
   * Fill the column instead of stopping at the measure.
   *
   * The cap is right for a field standing beside another one, and wrong for a
   * field that has been given the whole row: a control capped at 68 characters
   * inside an eleven-hundred-pixel column leaves a bare band running the height
   * of the form, which is a hole rather than a margin. The measure governs
   * **reading**, and nothing in a one-line input or a five-row textarea is read
   * the way a paragraph is.
   */
  wide?: boolean;
  children: (id: string, describedBy: string | undefined, invalid: boolean) => ReactNode;
}) {
  const id = useId();
  const hintId = useId();
  const errorId = useId();

  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    /*
      `content-start` is a fix, not a preference. Two fields sharing a grid row
      stretch to the taller of the two, and a grid whose rows are all `auto`
      distributes that extra height **across its own rows** under the default
      `align-content` — so the field without a hint grew a taller label row and a
      taller control, and the two inputs beside each other no longer sat on one
      line. Packing the rows to the start leaves the spare height at the foot,
      where nothing is drawn.
    */
    <div className={cn('grid content-start gap-2', wide ? 'max-w-none' : 'max-w-measure')}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required ? (
          <span aria-hidden="true" className="ms-1 text-danger">
            {sharedCopy.requiredMark}
          </span>
        ) : null}
      </label>

      {children(id, describedBy, Boolean(error))}

      {hint ? (
        <p id={hintId} className="mb-0 max-w-none text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={errorId} className="mb-0 max-w-none text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * How many rows are shown against how many exist.
 *
 * **Not an empty state and not a pager.** No catalog row gives the content list
 * a traversal control, so it states its own truncation instead of inventing
 * one, and it states it even when the two figures are equal — a line that
 * appears only on truncation teaches the operator to read its absence as
 * completeness on a page where it simply was not rendered.
 */
export function TruncationLine({
  shown,
  total,
  children,
}: {
  shown: number;
  total: number;
  children?: ReactNode;
}) {
  return (
    <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
      {sharedCopy.truncation.prefix} <bdi>{formatNumber(shown)}</bdi> {sharedCopy.truncation.middle}{' '}
      <bdi>{formatNumber(total)}</bdi>
      {children ? <> — {children}</> : null}
    </p>
  );
}

/**
 * The four artwork purposes and where each one's image came from.
 *
 * The point of the table is that **one upload produces four purposes**: the
 * thumbnail is the only required one, and the rest derive from it by
 * proportional crop or arrive through the provider fill. Drawing the derivation
 * is what stops an operator hunting for three uploads that do not exist.
 *
 * The ratio is a machine string in the mono face — `2:3` is a shape a service
 * parses, not a quantity a person reads.
 */
export function ArtworkPurposeTable({ rows }: { rows: readonly ArtworkPurposeRow[] }) {
  return (
    <DTableScroll>
      <DTable caption={sharedCopy.artwork.caption} minInlineSize="20rem">
        <DTableHead>
          <tr>
            <th scope="col">{sharedCopy.artwork.columns.purpose}</th>
            <th scope="col">{sharedCopy.artwork.columns.ratio}</th>
            <th scope="col">{sharedCopy.artwork.columns.source}</th>
          </tr>
        </DTableHead>
        <DTableBody>
          {rows.map((row) => {
            const source = resolveArtworkSource(row.source);

            return (
              <tr key={row.purpose}>
                <DTableRowHeader>{resolveArtworkPurpose(row.purpose)}</DTableRowHeader>
                <td>
                  <bdi className="mono">{row.ratio}</bdi>
                </td>
                <td>
                  <StateBadge kind={source.kind}>{source.label}</StateBadge>
                </td>
              </tr>
            );
          })}
        </DTableBody>
      </DTable>
    </DTableScroll>
  );
}

/**
 * The credit block — a person, and the roles they hold **on this title**.
 *
 * Two decisions the mockup fixes and this component keeps:
 *
 * - **The add row carries no role picker.** A person is added first and their
 *   roles are pressed on their own row afterwards, because a person may hold
 *   more than one — the same name directs a title and writes it. A single
 *   picker in the add row would force one role and then need correcting on the
 *   row, asking the same question twice.
 * - **The role vocabulary is read**, never compiled in. `CREDIT_ROLES` arrives
 *   from the owning service's configuration; a member with no wording renders as
 *   its own token rather than being dropped.
 *
 * A person who is not in the register is created where registers are managed,
 * not here: their record serves every title they appear on, and creating it
 * inside one title's form hides that and duplicates them at the next title.
 */
export function CreditEditor({
  credits,
  roleVocabulary,
  profiles,
  registryHref,
  onToggleRole,
  onRemove,
  onAdd,
}: {
  credits: readonly CreditRelation[];
  roleVocabulary: readonly string[];
  /** The register, for the add row. */
  profiles: readonly CreditProfileRow[];
  registryHref: ReactNode;
  onToggleRole: (profileId: string, role: string) => void;
  onRemove: (profileId: string) => void;
  onAdd: (profileId: string) => void;
}) {
  const pickId = useId();
  const [pending, setPending] = useState('');

  const alreadyCredited = new Set(credits.map((credit) => credit.credit_profile_id));

  return (
    <div className="grid gap-4">
      <ul className="grid list-none gap-3 p-0">
        {credits.map((credit) => (
          <li
            key={credit.credit_profile_id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-surface p-3"
          >
            <span className="text-sm font-medium text-ink">{credit.display_name}</span>

            <div
              role="group"
              aria-label={sharedCopy.credits.rolesLabel(credit.display_name)}
              className="flex flex-wrap gap-2"
            >
              {roleVocabulary.map((role) => {
                const pressed = credit.roles.some((held) => held.role === role);

                return (
                  <button
                    key={role}
                    type="button"
                    aria-pressed={pressed}
                    onClick={() => onToggleRole(credit.credit_profile_id, role)}
                    className={cn(
                      'rounded-pill border px-3 py-1 text-xs',
                      pressed
                        ? 'border-accent bg-accent-soft text-accent-text'
                        : 'border-border bg-surface-raised text-ink-muted hover:bg-hover-bg',
                    )}
                  >
                    {resolveCreditRole(role)}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => onRemove(credit.credit_profile_id)}
              aria-label={sharedCopy.credits.remove(credit.display_name)}
              className="hit-target ms-auto flex items-center justify-center rounded-sm text-ink-muted hover:bg-danger-soft hover:text-danger"
            >
              <CloseGlyph />
            </button>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor={pickId} className="sr-only">
          {sharedCopy.credits.pickLabel}
        </label>
        <select
          id={pickId}
          value={pending}
          onChange={(event) => setPending(event.target.value)}
          className={cn(FIELD_CONTROL_CLASS, 'max-w-[18rem]')}
        >
          <option value="">{sharedCopy.credits.pickPlaceholder}</option>
          {profiles
            .filter((profile) => !alreadyCredited.has(profile.id))
            .map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.display_name}
              </option>
            ))}
        </select>

        <button
          type="button"
          disabled={pending === ''}
          onClick={() => {
            onAdd(pending);
            setPending('');
          }}
          className="hit-target rounded-sm border border-border-strong bg-surface-raised px-4 text-sm font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-45"
        >
          {sharedCopy.credits.add}
        </button>
      </div>

      <p className="mb-0 max-w-measure text-xs text-ink-muted">
        {sharedCopy.credits.registryLead} {registryHref} {sharedCopy.credits.registryTail}
      </p>
    </div>
  );
}

/**
 * The upload zone — **three paths, because dragging is only one of them**.
 *
 * Drop, click the picker, or activate the zone from the keyboard: the zone
 * carries `role="button"` and a tab stop so the third path exists at all. The
 * accepted formats are printed from the published upload policy rather than
 * left for a rejected upload to reveal.
 *
 * **One zone per destination.** A single zone serving a whole page leaves «where
 * does this file land?» unanswerable, and any answer given afterwards can be
 * given wrongly — so the drop *is* the assignment, and the hint names the
 * destination in words.
 */
export function UploadZone({
  label,
  hint,
  accept,
  compact = false,
  progress = null,
  error = null,
  onFiles,
}: {
  /** Names the destination in words — «أفلت حلقات الموسم الأوّل هنا». */
  label: string;
  hint: ReactNode;
  accept: string;
  compact?: boolean;
  /**
   * Fractional progress of the upload **this zone** started, `0..1`, or `null`
   * when nothing is in flight.
   *
   * Drawn here rather than in a toast or a page-wide spinner because every
   * destination already has its own zone so the drop *is* the assignment. A
   * title being filled can have a trailer, a film file, and a zone per season
   * uploading at once, and one shared indicator cannot say which of them it is
   * reporting.
   */
  progress?: number | null;
  /**
   * What went wrong with **this** upload, drawn under this zone.
   *
   * A rejected file is a rejection of that file. Reporting it page-wide is what
   * makes an operator re-do a finished upload to fix an unrelated field.
   */
  error?: string | null;
  onFiles: (files: FileList) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const errorId = useId();
  const [dragging, setDragging] = useState(false);
  const busy = typeof progress === 'number';

  return (
    <div className="grid gap-2">
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        // No `aria-invalid`: the role here is `button`, which does not support
        // it. The refusal reaches assistive technology through the alert below and
        // through this element's own description instead.
        aria-describedby={[hintId, error ? errorId : null].filter(Boolean).join(' ')}
        aria-busy={busy}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          inputRef.current?.click();
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files);
        }}
        className={cn(
          'grid cursor-pointer justify-items-center gap-2 rounded-md border border-dashed border-border-strong bg-surface text-center',
          compact ? 'p-4' : 'p-6',
          dragging && 'border-accent bg-accent-soft',
          error && 'border-danger',
        )}
      >
        <UploadGlyph />
        <p className="mb-0 max-w-none text-sm font-semibold text-ink">{label}</p>
        <p className="mb-0 max-w-none text-xs text-ink-muted">{sharedCopy.artwork.pick}</p>
      </div>

      {/*
        **The picker, not a control, and not inside the zone.** The zone above is
        the control — it carries the role, the name, the keyboard handling and
        the drop — and this input exists only so that pressing the zone can open
        the file dialog.

        It is a **sibling** of the zone, never a child. `role="button"` must not
        contain a focusable descendant, and `axe` counts an input as focusable
        there whether or not it carries `tabIndex={-1}` and `aria-hidden`: a
        negative tabindex does not stop assistive technology reaching an element
        inside an interactive control. Nesting it and hiding it moved the
        violation from `label` to `nested-interactive` rather than ending it;
        only taking it out of the widget does that.

        `tabIndex={-1}` **and** `aria-hidden` stay, never one alone: they keep
        the input out of the tab order and out of the accessibility tree, where
        a bare `input[type=file]` would otherwise be a second, nameless widget.
        An `aria-hidden` element that is still focusable is its own violation
        (`aria-hidden-focus`), which is how this defect usually moves rather
        than goes.
      */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) onFiles(event.target.files);
          // The same file picked twice in a row must still fire a change.
          event.target.value = '';
        }}
      />

      {busy ? <UploadProgress fraction={progress ?? 0} /> : null}

      <p id={hintId} className="mb-0 max-w-measure text-xs text-ink-muted">
        {hint}
      </p>

      {error ? (
        <p id={errorId} role="alert" className="mb-0 max-w-measure text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * How much of the file has reached the server, on the zone it was dropped into.
 *
 * A native `<progress>`, so the value is exposed as a number rather than as a
 * sentence re-read on every tick. The percentage beside it is written in
 * Arabic-Indic digits like every other human-facing figure on this console; the
 * machine-facing one is the element's own `value`.
 */
function UploadProgress({ fraction }: { fraction: number }) {
  const percent = Math.min(100, Math.max(0, Math.round(fraction * 100)));

  return (
    <div className="grid gap-1">
      <progress
        value={percent}
        max={100}
        aria-label={sharedCopy.artwork.uploading}
        className="h-1.5 w-full overflow-hidden rounded-sm"
      />
      <p className="mb-0 max-w-none text-xs text-ink-muted">
        {sharedCopy.artwork.uploading} {formatPercent(percent / 100)}
      </p>
    </div>
  );
}

function UploadGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.75em"
      height="1.75em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="text-ink-muted"
    >
      <path d="M12 16V4" />
      <path d="M7.5 8.5L12 4l4.5 4.5" />
      <path d="M4 15v3.5A1.5 1.5 0 005.5 20h13a1.5 1.5 0 001.5-1.5V15" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
